/**
 * The workspaces' motion (framer-motion 10), one vocabulary for every shell. Motion connects a choice to its result:
 * a view chosen in the sidebar settles into place. It never delays an answer: content is in the DOM and in the
 * accessibility tree from the first frame, final values show at once, and under prefers-reduced-motion nothing moves.
 *
 *   <ViewTransition viewKey={view}>…the view…</ViewTransition>
 */
import React, { useEffect, useRef } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';

export const EASE_OUT = [0.33, 1, 0.68, 1] as const; // = --ease-out in index.css

/**
 * Settles the content into place when `viewKey` changes (a short fade and 6px rise). The subtree is NOT remounted:
 * state inside it (a half-filled form, a scroll position, an open panel) survives the view change exactly as it did
 * before this wrapper existed. Nothing animates on first paint. No transform is kept once settled, so sticky children
 * and fixed sheets inside keep working.
 */
export function ViewTransition({ viewKey, className, children }: { viewKey: string; className?: string; children: React.ReactNode }) {
  const reduce = useReducedMotion();
  const controls = useAnimationControls();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (reduce) return;
    controls.set({ opacity: 0, y: 6 });
    controls.start({ opacity: 1, y: 0, transition: { duration: 0.22, ease: EASE_OUT }, transitionEnd: { transform: 'none' } });
  }, [viewKey, reduce, controls]);
  return <motion.div className={className} animate={controls}>{children}</motion.div>;
}
